import React, { useState } from 'react';
import { CheckCircle2, Send } from 'lucide-react';
import { Button } from './Button';
import { Textarea } from './Textarea';
type FormValues = {
  name: string;
  phone: string;
  projectType: string;
  details: string;
};
type FormErrors = Partial<Record<keyof FormValues, string>>;
const initialValues: FormValues = {
  name: '',
  phone: '',
  projectType: '',
  details: ''
};
export function EstimateForm() {
  const [values, setValues] = useState<FormValues>(initialValues);
  const [errors, setErrors] = useState<FormErrors>({});
  const [submitted, setSubmitted] = useState(false);
  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const nextErrors: FormErrors = {};
    if (!values.name.trim()) nextErrors.name = 'Please enter your name.';
    if (!values.phone.trim()) nextErrors.phone = 'Please enter a phone number.';
    if (!values.projectType) nextErrors.projectType = 'Please select a project type.';
    if (!values.details.trim()) nextErrors.details = 'Tell us a little about your project.';
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length === 0) {
      setSubmitted(true);
      setValues(initialValues);
    }
  }
  if (submitted) {
    return <div className="flex min-h-[402px] flex-col items-center justify-center px-6 text-center" data-id="element-1">
        <div className="mb-5 rounded-full bg-[#359C9B]/15 p-4 text-[#359C9B]" data-id="element-2">
          <CheckCircle2 className="h-9 w-9" aria-hidden="true" data-id="element-3" />
        </div>
        <h3 className="text-2xl font-semibold text-[#142843]" data-id="element-4">
          Request received
        </h3>
        <p className="mt-3 max-w-sm text-sm leading-6 text-[#52636b]" data-id="element-5">
          Thank you for reaching out. We’ll use the details you shared to start
          the conversation about your project.
        </p>
        <button type="button" onClick={() => setSubmitted(false)} className="mt-6 text-sm font-semibold text-[#142843] underline decoration-[#359C9B] decoration-2 underline-offset-4" data-id="element-6">
          Send another request
        </button>
      </div>;
  }
  return <form noValidate onSubmit={handleSubmit} className="space-y-4" data-id="element-7">
      <div data-id="element-8">
        <label htmlFor="name" className="mb-1.5 block text-sm font-semibold text-[#142843]" data-id="element-9">
          Your name
        </label>
        <input id="name" value={values.name} onChange={event => setValues({
        ...values,
        name: event.target.value
      })} aria-invalid={Boolean(errors.name)} aria-describedby={errors.name ? 'name-error' : undefined} className="h-11 w-full rounded-lg border border-[#D6DEE0] bg-white px-3 text-sm text-[#142843] outline-none transition focus:border-[#359C9B] focus:ring-4 focus:ring-[#359C9B]/15" data-id="element-10" />
        {errors.name && <p id="name-error" className="mt-1 text-xs text-red-700" data-id="element-11">
            {errors.name}
          </p>}
      </div>

      <div data-id="element-12">
        <label htmlFor="phone" className="mb-1.5 block text-sm font-semibold text-[#142843]" data-id="element-13">
          Phone number
        </label>
        <input id="phone" type="tel" value={values.phone} onChange={event => setValues({
        ...values,
        phone: event.target.value
      })} aria-invalid={Boolean(errors.phone)} aria-describedby={errors.phone ? 'phone-error' : undefined} className="h-11 w-full rounded-lg border border-[#D6DEE0] bg-white px-3 text-sm text-[#142843] outline-none transition focus:border-[#359C9B] focus:ring-4 focus:ring-[#359C9B]/15" data-id="element-14" />
        {errors.phone && <p id="phone-error" className="mt-1 text-xs text-red-700" data-id="element-15">
            {errors.phone}
          </p>}
      </div>

      <div data-id="element-16">
        <label htmlFor="projectType" className="mb-1.5 block text-sm font-semibold text-[#142843]" data-id="element-17">
          Project type
        </label>
        <select id="projectType" value={values.projectType} onChange={event => setValues({
        ...values,
        projectType: event.target.value
      })} aria-invalid={Boolean(errors.projectType)} className="h-11 w-full rounded-lg border border-[#D6DEE0] bg-white px-3 text-sm text-[#142843] outline-none transition focus:border-[#359C9B] focus:ring-4 focus:ring-[#359C9B]/15" data-id="element-18">
          <option value="" data-id="element-19">Select a service</option>
          <option data-id="element-20">Fencing &amp; Gates</option>
          <option data-id="element-21">Decks &amp; Outdoor Structures</option>
          <option data-id="element-22">Covered Patios &amp; Outdoor Living</option>
          <option data-id="element-23">Landscape Design &amp; Enhancement</option>
          <option data-id="element-24">Other Outdoor Project</option>
        </select>
        {errors.projectType && <p className="mt-1 text-xs text-red-700" data-id="element-25">{errors.projectType}</p>}
      </div>

      <div data-id="element-26">
        <label htmlFor="details" className="mb-1.5 block text-sm font-semibold text-[#142843]" data-id="element-27">
          Project details
        </label>
        <Textarea id="details" value={values.details} onChange={event => setValues({
        ...values,
        details: event.target.value
      })} aria-invalid={Boolean(errors.details)} className="min-h-[104px] border-[#D6DEE0] focus-visible:border-[#359C9B] focus-visible:ring-[#359C9B]/20" placeholder="What are you hoping to create?" data-id="element-28" />
        {errors.details && <p className="mt-1 text-xs text-red-700" data-id="element-29">{errors.details}</p>}
      </div>

      <Button type="submit" className="h-12 w-full bg-[#359C9B] text-white hover:bg-[#2a8584]" data-id="element-30">
        Send estimate request{' '}
        <Send className="ml-2 h-4 w-4" aria-hidden="true" data-id="element-31" />
      </Button>
    </form>;
}